import { jsx as _jsx } from "react/jsx-runtime";
import { useImperativeHandle, useRef } from 'react';
import styled from 'styled-components';
export function Stage({ controllerRef: foreignControllerRef, children, }) {
    const rootRef = useRef(null);
    const innerRef = useRef(null);
    const localControllerRef = useRef(null);
    const controllerRef = foreignControllerRef || localControllerRef;
    const animationRef = useRef(null);
    const shrunkRef = useRef(false);
    useImperativeHandle(controllerRef, () => ({
        shrink: () => {
            if (shrunkRef.current) {
                return;
            }
            shrunkRef.current = true;
            animationRef.current?.cancel();
            animationRef.current = animate({
                onUpdate: (x) => {
                    rootRef.current?.style.setProperty('--Stage_WidthOffset', `-${x * 25.25}rem`);
                },
            });
        },
        unshrink: () => {
            if (!shrunkRef.current) {
                return;
            }
            shrunkRef.current = false;
            animationRef.current?.cancel();
            animationRef.current = animate({
                onUpdate: (x) => {
                    rootRef.current?.style.setProperty('--Stage_WidthOffset', `-${(1 - x) * 25.25}rem`);
                },
            });
        },
    }), []);
    return (_jsx(StageRoot, { ref: rootRef, children: _jsx(StageInner, { ref: innerRef, children: children }) }));
}
const StageInner = styled.div `
    width: var(--Stage_InnerWidth, 100%);
    height: var(--Stage_InnerHeight, 100%);
    overflow: hidden;
    border-radius: var(--Stage_BorderRadius, 0);

    video {
        border-radius: 0;
        position: relative;
        left: -1px;
        top: -1px;
        width: calc(100% + 2px);
        height: calc(100% + 2px);
    }
`;
const StageRoot = styled.div `
    align-items: center;
    background-color: var(--Stage_Background, transparent);
    border-radius: var(--Stage_BorderRadius, 0);
    display: flex;
    height: calc(
        100% - var(--Stage_PaddingTop, 1.5rem) -
            var(--Stage_PaddingBottom, 1.5rem)
    );
    justify-content: center;
    left: var(--Stage_PaddingLeft, 1.5rem);
    overflow: hidden;
    position: absolute;
    top: var(--Stage_PaddingTop, 1.5rem);
    width: calc(
        100% - var(--Stage_PaddingLeft, 1.5rem) -
            var(--Stage_PaddingRight, 1.5rem)
    );

    @media (min-width: 60rem) {
        width: calc(
            100% - var(--Stage_PaddingLeft, 1.5rem) -
                var(--Stage_PaddingRight, 1.5rem) +
                var(--Stage_WidthOffset, 0rem)
        );
    }
`;
function animate({ onUpdate, onComplete }) {
    const duration = 100;
    const startTime = performance.now();
    let animationFrameRequestId;
    function step() {
        const elapsed = performance.now() - startTime;
        const progress = Math.min(elapsed / duration, 1); // Normalize to [0, 1]
        onUpdate?.(easeInOutCubic(progress));
        if (progress < 1) {
            animationFrameRequestId = requestAnimationFrame(step);
        }
        else {
            onUpdate?.(1);
            onComplete?.();
            animationFrameRequestId = undefined;
        }
    }
    animationFrameRequestId = requestAnimationFrame(step);
    return {
        cancel: () => {
            if (animationFrameRequestId != null) {
                cancelAnimationFrame(animationFrameRequestId);
                animationFrameRequestId = undefined;
            }
        },
    };
}
function easeInOutCubic(x) {
    return x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2;
}
